import dagre from 'dagre';

const NODE_WIDTH = 220;
const NODE_HEIGHT = 140;  // Increased to account for header + content

/**
 * Auto-layout BOM nodes in a horizontal tree structure
 * Parent items on the right, children (components) on the left
 */
export const layoutBOM = (nodes, edges, direction = 'LR') => {
  if (nodes.length === 0) {
    return { nodes, edges };
  }

  // Create a new dagre graph
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Configure the layout
  // LR = Left to Right (children on left, parents on right)
  // This matches our mental model: components flow INTO assemblies
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 40,        // Vertical spacing between nodes
    ranksep: 180,       // Horizontal spacing between ranks
    marginx: 50,
    marginy: 50,
    align: 'UL',
    acyclicer: 'greedy',
    ranker: 'tight-tree',
  });

  // Add nodes to the graph
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { 
      width: NODE_WIDTH, 
      height: NODE_HEIGHT,
    });
  });

  // Add edges to the graph
  // In our flow, source = child, target = parent
  // For layout purposes, we want parents on the right
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Run the layout algorithm
  dagre.layout(dagreGraph);

  // Apply the calculated positions to our nodes
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    
    // dagre gives center positions, React Flow wants top-left
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return {
    nodes: layoutedNodes,
    edges: edges,
  };
};

/**
 * Layout with multiple root detection
 * Useful when loading BOMs that might have multiple disconnected trees
 */
export const layoutBOMWithRoots = (nodes, edges) => {
  if (nodes.length === 0) {
    return { nodes, edges };
  }

  // Find root nodes (nodes that are not a child of anything)
  // In our edge structure: source = child, target = parent
  // So roots are nodes that never appear as a source
  const childNodeIds = new Set(edges.map(e => e.source));
  const rootNodes = nodes.filter(n => !childNodeIds.has(n.id));

  if (rootNodes.length <= 1) {
    // Single tree, use standard layout
    return layoutBOM(nodes, edges);
  }

  // Multiple trees - layout each separately and offset
  const layouts = [];
  let currentYOffset = 0;

  rootNodes.forEach((rootNode, index) => {
    // Find all nodes connected to this root
    const connectedNodes = findConnectedNodes(rootNode.id, nodes, edges);
    const connectedEdges = edges.filter(e => 
      connectedNodes.has(e.source) && connectedNodes.has(e.target)
    );

    const treeNodes = nodes.filter(n => connectedNodes.has(n.id));
    
    // Layout this tree
    const { nodes: layoutedTreeNodes } = layoutBOM(treeNodes, connectedEdges);

    // Find bounds of this tree
    let minY = Infinity, maxY = -Infinity;
    layoutedTreeNodes.forEach(n => {
      minY = Math.min(minY, n.position.y);
      maxY = Math.max(maxY, n.position.y + NODE_HEIGHT);
    });

    // Offset all nodes in this tree
    const offsetNodes = layoutedTreeNodes.map(n => ({
      ...n,
      position: {
        x: n.position.x,
        y: n.position.y - minY + currentYOffset,
      },
    }));

    layouts.push(...offsetNodes);
    currentYOffset += (maxY - minY) + 100; // Add spacing between trees
  });

  return {
    nodes: layouts,
    edges: edges,
  };
};

/**
 * Find all nodes connected to a given root node
 */
const findConnectedNodes = (rootId, nodes, edges) => {
  const connected = new Set([rootId]);
  const queue = [rootId];

  while (queue.length > 0) {
    const currentId = queue.shift();
    
    // Find children (nodes where this is the target/parent)
    edges.forEach(e => {
      if (e.target === currentId && !connected.has(e.source)) {
        connected.add(e.source);
        queue.push(e.source);
      }
    });

    // Find parents (nodes where this is the source/child)
    edges.forEach(e => {
      if (e.source === currentId && !connected.has(e.target)) {
        connected.add(e.target);
        queue.push(e.target);
      }
    });
  }

  return connected;
};

/**
 * Get the depth of each node in the tree
 * Useful for coloring or sizing nodes by level
 */
export const calculateNodeDepths = (nodes, edges) => {
  const depths = new Map();
  
  // Find root nodes
  const childNodeIds = new Set(edges.map(e => e.source));
  const roots = nodes.filter(n => !childNodeIds.has(n.id));

  // BFS from roots
  roots.forEach(root => {
    const queue = [{ id: root.id, depth: 0 }];
    
    while (queue.length > 0) {
      const { id, depth } = queue.shift();
      
      if (depths.has(id)) continue;
      depths.set(id, depth);

      // Find children
      edges.forEach(e => {
        if (e.target === id && !depths.has(e.source)) {
          queue.push({ id: e.source, depth: depth + 1 });
        }
      });
    }
  });

  return depths;
};